import {
  type ReactNode,
  Suspense,
  lazy,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import {
  ImageOff,
  Info,
  Pencil,
  RotateCcw,
  RotateCw,
  Sparkles,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import type { MediaSize } from 'react-easy-crop';

import { buildImageTransform } from '@modules/_shared/components/image-upload/buildImageTransform';
import { CoverPreview } from '@modules/_shared/components/image-upload/components/CoverPreview';
import { CropPreview } from '@modules/_shared/components/image-upload/components/CropPreview';
import { ImageLoadingOverlay } from '@modules/_shared/components/image-upload/components/ImageLoadingOverlay';
import type {
  CropArea,
  ImageSource,
  ImageTransform,
} from '@modules/_shared/components/image-upload/types';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useLocale } from '@shared/hooks/locale';

const Cropper = lazy(() => import('react-easy-crop'));

const ZOOM_STEP = 0.1;
const ROTATION_STEP = 10;

const CROPPER_STYLE = {
  containerStyle: { background: 'transparent' },
  cropAreaStyle: {
    border: '2px solid rgba(255, 255, 255, 0.9)',
    boxShadow: '0 0 0 9999px rgba(15, 23, 41, 0.6)',
  },
};

const STAGE_BACKGROUND = '#0F1729';

const STEP_BUTTON_SX = {
  color: 'text.secondary',
  '&::after': { content: '""', position: 'absolute', inset: -7 },
  '&.Mui-focusVisible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
} as const;

const ACTION_BUTTON_SX = { minHeight: 40, flexShrink: 0 } as const;

type ImageUploadProps = {
  source: ImageSource;
  currentImageUrl?: string | null;
  open: boolean;
  onClose: () => void;
  onSave: (transform: ImageTransform) => void;
  onChangePhoto?: () => void;
  isLoading?: boolean;
  aspectRatio?: number;
  cropShape?: 'rect' | 'round';
  minZoom?: number;
  maxZoom?: number;
  title: string;
  enableCropping?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  infoText?: ReactNode;
  onEnhanceClick?: () => void;
  error?: string | null;
  previewLabel?: string | null;
  previewLogoUrl?: string | null;
};

const SliderLabel = ({ label, value }: { label: string; value: string }) => (
  <Stack
    direction="row"
    sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}
  >
    <Typography variant="body2">{label}</Typography>
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
  </Stack>
);

export const ImageUpload = memo(
  ({
    source,
    currentImageUrl,
    open,
    onClose,
    onSave,
    onChangePhoto,
    isLoading = false,
    aspectRatio = 1,
    cropShape = 'rect',
    minZoom = 1,
    maxZoom = 3,
    title,
    enableCropping = true,
    maxWidth = 'md',
    infoText,
    onEnhanceClick,
    error,
    previewLabel,
    previewLogoUrl,
  }: ImageUploadProps) => {
    const { t } = useLocale();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(minZoom);
    const [rotation, setRotation] = useState(0);
    const [croppedArea, setCroppedArea] = useState<CropArea | null>(null);
    const [mediaSize, setMediaSize] = useState<MediaSize | null>(null);
    const [isImageLoading, setIsImageLoading] = useState(true);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const isInitialMount = useRef(true);
    const imageUrlRef = useRef<string | null>(null);

    const imageUrl = useMemo(() => {
      // Clean up previous object URL
      if (imageUrlRef.current && imageUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrlRef.current);
      }

      let url = '';
      if (source.type === 'file') {
        url = URL.createObjectURL(source.data as File);
        imageUrlRef.current = url;
      } else if (source.type === 'url') {
        url = source.data as string;
      } else {
        url = currentImageUrl || '';
      }

      return url;
    }, [source, currentImageUrl]);

    useEffect(() => {
      setCrop({ x: 0, y: 0 });
      setZoom(minZoom);
      setRotation(0);
      setCroppedArea(null);
      setMediaSize(null);
      setIsImageLoading(true);
      setHasLoadError(false);
      // Reset interaction state when source changes
      setHasUserInteracted(false);
      isInitialMount.current = true;
    }, [source, minZoom]);

    // Cleanup object URLs on unmount
    useEffect(() => {
      return () => {
        if (imageUrlRef.current && imageUrlRef.current.startsWith('blob:')) {
          URL.revokeObjectURL(imageUrlRef.current);
        }
      };
    }, []);

    const handleImageLoad = useCallback(() => {
      setIsImageLoading(false);
      // Set initial mount to false after image loads
      setTimeout(() => {
        isInitialMount.current = false;
      }, 100);
    }, []);

    const handleImageError = useCallback(() => {
      setIsImageLoading(false);
      setHasLoadError(true);
    }, []);

    const mediaProps = useMemo(
      () => ({ onError: handleImageError }),
      [handleImageError],
    );

    const onCropComplete = useCallback(
      (_: CropArea, croppedAreaPixels: CropArea) => {
        setCroppedArea(croppedAreaPixels);
      },
      [],
    );

    const handleSave = useCallback(() => {
      onSave(
        buildImageTransform({
          hasUserInteracted,
          cropArea: croppedArea,
          scale: zoom,
          initialScale: 1,
          rotation,
        }),
      );
    }, [croppedArea, rotation, zoom, hasUserInteracted, onSave]);

    const changeZoom = (value: number, fromStep = false) => {
      const next = Math.min(maxZoom, Math.max(minZoom, value));
      setZoom(next);
      if (!fromStep || next !== minZoom) {
        setHasUserInteracted(true);
      }
    };

    const changeRotation = (value: number, fromStep = false) => {
      const next = Math.min(180, Math.max(-180, value));
      setRotation(next);
      if (!fromStep || next !== 0) {
        setHasUserInteracted(true);
      }
    };

    const hasSidePanel = enableCropping && aspectRatio <= 2;
    const previewWidth = cropShape === 'round' ? 72 : 96;

    const stage = (
      <>
        <Box
          sx={{
            position: 'relative',
            height: hasSidePanel
              ? { xs: 280, md: 420 }
              : enableCropping
              ? { xs: 200, md: 240 }
              : { xs: 240, md: 320 },
            borderRadius: '12px',
            overflow: 'hidden',
            bgcolor: enableCropping ? STAGE_BACKGROUND : 'grey.100',
            pointerEvents: isLoading ? 'none' : undefined,
          }}
          role="img"
        >
          <Suspense fallback={<LoadingSpinner />}>
            {enableCropping ? (
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspectRatio}
                cropShape={cropShape}
                onCropChange={newCrop => {
                  setCrop(newCrop);
                  if (!isInitialMount.current) {
                    setHasUserInteracted(true);
                  }
                }}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onMediaLoaded={size => {
                  setMediaSize(size);
                  handleImageLoad();
                }}
                mediaProps={mediaProps}
                minZoom={minZoom}
                maxZoom={maxZoom}
                showGrid={false}
                style={CROPPER_STYLE}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                }}
              >
                <img
                  src={imageUrl}
                  alt={title}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
            <ImageLoadingOverlay visible={isImageLoading} />
          </Suspense>
          <Fade in={isLoading} timeout={150} unmountOnExit>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(15, 23, 41, 0.55)',
              }}
            >
              <CircularProgress
                size={28}
                thickness={5}
                sx={{ color: 'common.white' }}
              />
            </Box>
          </Fade>
          {hasLoadError && (
            <Stack
              spacing={2}
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                pointerEvents: 'auto',
              }}
            >
              <ImageOff size={24} color="currentColor" />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('Could not load this photo')}
              </Typography>
              {onChangePhoto && (
                <Button variant="outlined" onClick={onChangePhoto}>
                  {t('Choose another photo')}
                </Button>
              )}
            </Stack>
          )}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2, textWrap: 'balance' }}>
            {error}
          </Alert>
        )}
      </>
    );

    const sliders = (
      <Stack spacing={2}>
        <Box>
          <SliderLabel label={t('Zoom')} value={`${zoom.toFixed(1)}×`} />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <IconButton
              size="small"
              aria-label={t('Zoom out')}
              onClick={() => changeZoom(zoom - ZOOM_STEP, true)}
              disabled={isLoading || zoom <= minZoom}
              sx={STEP_BUTTON_SX}
            >
              <ZoomOut size={16} />
            </IconButton>
            <Slider
              value={zoom}
              onChange={(_, value) => changeZoom(value as number)}
              min={minZoom}
              max={maxZoom}
              step={ZOOM_STEP}
              disabled={isLoading}
              aria-label={t('Zoom')}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              aria-label={t('Zoom in')}
              onClick={() => changeZoom(zoom + ZOOM_STEP, true)}
              disabled={isLoading || zoom >= maxZoom}
              sx={STEP_BUTTON_SX}
            >
              <ZoomIn size={16} />
            </IconButton>
          </Stack>
        </Box>
        <Box>
          <SliderLabel label={t('Rotation')} value={`${rotation}°`} />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <IconButton
              size="small"
              aria-label={t('Rotate left')}
              onClick={() => changeRotation(rotation - ROTATION_STEP, true)}
              disabled={isLoading || rotation <= -180}
              sx={STEP_BUTTON_SX}
            >
              <RotateCcw size={16} />
            </IconButton>
            <Slider
              value={rotation}
              onChange={(_, value) => changeRotation(value as number)}
              min={-180}
              max={180}
              step={1}
              track={false}
              disabled={isLoading}
              aria-label={t('Rotation')}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              aria-label={t('Rotate right')}
              onClick={() => changeRotation(rotation + ROTATION_STEP, true)}
              disabled={isLoading || rotation >= 180}
              sx={STEP_BUTTON_SX}
            >
              <RotateCw size={16} />
            </IconButton>
          </Stack>
        </Box>
      </Stack>
    );

    const infoLine = infoText && (
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', color: 'text.secondary', minWidth: 0 }}
      >
        <Info size={16} style={{ flexShrink: 0 }} />
        <Typography variant="body2" sx={{ textWrap: 'pretty' }}>
          {infoText}
        </Typography>
      </Stack>
    );

    const actionButtons = (fullWidth: boolean) => (
      <>
        {onEnhanceClick && !hasLoadError && (
          <Button
            variant="outlined"
            fullWidth={fullWidth}
            onClick={onEnhanceClick}
            disabled={isLoading}
            startIcon={<Sparkles size={16} />}
            sx={ACTION_BUTTON_SX}
          >
            {t('Enhance with AI')}
          </Button>
        )}
        {onChangePhoto && (
          <Button
            variant="text"
            fullWidth={fullWidth}
            onClick={onChangePhoto}
            disabled={isLoading}
            startIcon={<Pencil size={16} />}
            sx={ACTION_BUTTON_SX}
          >
            {t('Change photo')}
          </Button>
        )}
      </>
    );
    const hasActions = Boolean(onEnhanceClick || onChangePhoto);

    return (
      <SlideDialog
        open={open}
        onClose={onClose}
        title={title}
        onSave={handleSave}
        loading={isLoading}
        saveDisabled={hasLoadError || isImageLoading}
        saveIcon={null}
        maxWidth={maxWidth}
        fullWidth
        cancelText={t('Cancel')}
        saveText={t('Save')}
      >
        {hasSidePanel ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{ bgcolor: 'background.paper' }}
          >
            <Box sx={{ flex: 1, minWidth: 0, p: 2.5 }}>{stage}</Box>
            <Stack
              spacing={3}
              sx={{
                width: { xs: 'auto', md: 280 },
                flexShrink: 0,
                p: 2.5,
                borderStyle: 'solid',
                borderColor: 'divider',
                borderWidth: 0,
                borderTopWidth: { xs: 1, md: 0 },
                borderLeftWidth: { xs: 0, md: 1 },
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: 'center' }}
              >
                {!isImageLoading && !hasLoadError ? (
                  <CropPreview
                    imageUrl={imageUrl}
                    mediaSize={mediaSize}
                    cropArea={croppedArea}
                    rotation={rotation}
                    width={previewWidth}
                    aspectRatio={aspectRatio}
                    round={cropShape === 'round'}
                  />
                ) : (
                  <Box
                    sx={{
                      width: previewWidth,
                      aspectRatio: String(aspectRatio),
                      flexShrink: 0,
                      borderRadius: cropShape === 'round' ? '50%' : '8px',
                      bgcolor: 'grey.100',
                    }}
                  />
                )}
                <Box sx={{ minWidth: 0 }}>
                  {previewLabel && (
                    <Typography noWrap sx={{ fontWeight: 500 }}>
                      {previewLabel}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('Preview')}
                  </Typography>
                </Box>
              </Stack>
              {sliders}
              {infoLine}
              {hasActions && (
                <Stack spacing={1} sx={{ mt: { md: 'auto' } }}>
                  {actionButtons(true)}
                </Stack>
              )}
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={3} sx={{ p: 2.5, bgcolor: 'background.paper' }}>
            <Box>{stage}</Box>
            {enableCropping && !hasLoadError && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
                <CoverPreview
                  imageUrl={imageUrl}
                  mediaSize={mediaSize}
                  cropArea={croppedArea}
                  rotation={rotation}
                  aspectRatio={aspectRatio}
                  isImageLoading={isImageLoading}
                  label={previewLabel}
                  logoUrl={previewLogoUrl}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>{sliders}</Box>
              </Stack>
            )}
            {(infoLine || hasActions) && (
              <>
                {enableCropping && <Divider />}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{
                    alignItems: { sm: 'center' },
                    justifyContent: 'space-between',
                  }}
                >
                  {infoLine || <Box />}
                  {hasActions && (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ flexShrink: 0, mr: -1 }}
                    >
                      {actionButtons(false)}
                    </Stack>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </SlideDialog>
    );
  },
);
